import * as Yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack5';
import { useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
// hooks
import useAuth from 'src/hooks/useAuth';
import { useTranslation, Trans } from 'react-i18next';
// material
import { Stack, Alert, RadioGroup, FormControlLabel, Radio, Typography, Checkbox } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { MIconButton } from 'src/components/@material-extend';
import { makeStyles } from '@mui/styles';
import AlertTitle from '@mui/material/AlertTitle';
// others
import { AVAILABLE_MFA_METHODS, MFA_METHOD_EMAIL, MFA_METHOD_TOTP } from 'src/@types/authentication';
import { MfaSetNavigation } from '../../SelectMfaMethod';
import { HelpButton } from '../HelpButton';
import wait from 'src/utils/wait';

type InitialValues = {
  mfaMethod: string;
  confirmChange: boolean;
  afterSubmit?: string;
};

type MfaMethodSelectFormProps = {
  navigationPaths: MfaSetNavigation;
  registeredMfaMethod: AVAILABLE_MFA_METHODS | null; // 登録済みMFA方式
};

const useStyles = makeStyles({
  root: {
    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    border: 0,
    borderRadius: 8,
    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
    color: 'white',
    height: 48,
    padding: '0 30px',
    '&.Mui-disabled': {
      background: '#f3f3f3',
      color: '#dadada',
    },
  },
  caution: {
    fontSize: '0.8rem',
  },
});

/**
 * MFA方式選択Form
 * @returns MfaMethodSelectForm
 */
export const MfaMethodSelectForm = ({ navigationPaths, registeredMfaMethod }: MfaMethodSelectFormProps) => {
  // hooks
  const { updateMfaMethod, logout, isAuthenticated, isRequiredMfa, isMfaAuthenticated } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const MfaMethodSchema = Yup.object().shape({
    mfaMethod: Yup.string().required(`Choose your MFA method.`),
  });

  // 選択可能なMFA方式
  const MFA_METHOD_ITEMS = {
    // TOTP: { id: MFA_METHOD_TOTP, itemNm: t('mfa.method.TOTP'), guide: 'mfa.selectMethod.infoTOTP' },
    EMAIL: { id: MFA_METHOD_EMAIL, itemNm: t('mfa.method.Email'), guide: 'mfa.selectMethod.infoEmail' },
  };

  // MFA方式 初期選択値
  const MFA_METHOD_INIT = MFA_METHOD_ITEMS.EMAIL;

  // State
  //  画面で選択中のMFA方式
  const [mfaMethod, setMfaMethod] = useState(MFA_METHOD_INIT.id);
  //  確認チェックボックス
  const [isConfirmed, setIsConfirmed] = useState(false);
  //  認証完了済みフラグ(MFA不要でID/Pass認証済み、またはMFA必須でMFA認証済みの場合True)
  const isAuthed = (!isRequiredMfa && isAuthenticated) || (isRequiredMfa && isMfaAuthenticated);

  // 指定したMFA方式から画面用項目取得
  const getMfaMethodItem = useCallback((method: AVAILABLE_MFA_METHODS | null) => {
    switch (method) {
      // case MFA_METHOD_TOTP:
      //   return MFA_METHOD_ITEMS.TOTP;
      case MFA_METHOD_EMAIL:
        return MFA_METHOD_ITEMS.EMAIL;
      default:
        // return MFA_METHOD_ITEMS.TOTP;
        return MFA_METHOD_ITEMS.EMAIL;
    }
  }, []);

  // MFA方法設定完了時のメッセージ
  const mfaMethodRegisteredMsg = useCallback(
    (method: AVAILABLE_MFA_METHODS) => {
      // 未認証で、MFA方法:Eメールを選択した場合、登録完了メッセージに再ログインを促す文言を付加
      const otpMsg = !isAuthed && method === MFA_METHOD_EMAIL ? t('mfa.selectMethod.requestReLogin') : '';
      return t('mfa.selectMethod.complete', { option: otpMsg });
    },
    [isAuthed],
  );

  // 初期表示処理
  useEffect(() => {
    // 登録済みMFA方法があれば初期値に設定
    if (isMfaAuthenticated) {
      const methodItem = getMfaMethodItem(registeredMfaMethod);
      values.mfaMethod = methodItem.id;
      setMfaMethod(methodItem.id);
    }
  }, [isMfaAuthenticated, registeredMfaMethod]);

  // MFA方法選択時のイベント
  const handleChangeMethod = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value;
    values.mfaMethod = value;
    setMfaMethod(value);
  }, []);

  // MFA方法を変更する際の同意チェックボックスのイベント
  const handleChangeConfirm = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const isConfirm = (event.target as HTMLInputElement).checked;
    setIsConfirmed(isConfirm);
  }, []);

  // ToolTip表示Component
  const toolTip = useCallback((msgKey: string) => {
    return (
      <Typography
        variant="caption"
        sx={{
          whiteSpace: 'pre-wrap',
          fontSize: '0.9rem',
        }}>
        {/* 改行コードの読み込み可能なTransを利用 */}
        <Trans i18nKey={msgKey} />
      </Typography>
    );
  }, []);

  const formik = useFormik<InitialValues>({
    enableReinitialize: true, // 初期値再設定を可能に
    initialValues: {
      mfaMethod: mfaMethod, // 初期値
      confirmChange: isConfirmed,
    },
    validationSchema: MfaMethodSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        // if (values.mfaMethod === MFA_METHOD_ITEMS.TOTP.id) {
        //   // TOTP選択時:
        //   //   MFAデバイス登録画面へ遷移
        //   navigate(navigationPaths.next, { replace: true, state: { navigationPaths } });
        //   return;
        // } else if (values.mfaMethod === MFA_METHOD_ITEMS.EMAIL.id) {
        if (values.mfaMethod === MFA_METHOD_ITEMS.EMAIL.id) {
          // EMAIL選択時:
          //   MFAにEMAILを設定
          await updateMfaMethod(MFA_METHOD_EMAIL);
          //   設定成功時は、メッセージを表示
          enqueueSnackbar(mfaMethodRegisteredMsg(MFA_METHOD_EMAIL), {
            variant: 'success',
            autoHideDuration: 10000, // 10秒後に自動close
            action: (key) => (
              <MIconButton
                size="small"
                onClick={() => {
                  closeSnackbar(key);
                }}>
                <Icon icon={closeFill} />
              </MIconButton>
            ),
          });

          // 認証済みの場合は元画面に遷移、未認証の場合(=初回MFA方法選択時は)一旦ログアウト
          if (isAuthed) {
            navigate(navigationPaths.caller ?? '/', { replace: true });
            return;
          }
          // ログアウト
          await wait(5000); // メッセージ表示のため5秒待機
          await logout();
          return;
        }
      } catch (err: any) {
        console.error(err);
        resetForm();
        setSubmitting(false);
        setErrors({ afterSubmit: err.message });
      }
    },
  });

  const { errors, values, isSubmitting, handleSubmit, getFieldProps } = formik;
  const classes = useStyles();
  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <label>{t('mfa.selectMethod.request')}</label>
          {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}
          <RadioGroup
            aria-labelledby="radio-buttons-group-mfa"
            name="controlled-radio-buttons-group-mfa"
            value={mfaMethod}
            onChange={handleChangeMethod}>
            {Object.values(MFA_METHOD_ITEMS).map((item) => {
              return (
                <FormControlLabel
                  key={item.id}
                  value={item.id}
                  control={<Radio />}
                  label={
                    <>
                      {item.itemNm}
                      <HelpButton guidanceText={toolTip(item.guide)} placement={'bottom'} />
                    </>
                  }
                />
              );
            })}
          </RadioGroup>
          {/* MFA未設定の場合に表示するメッセージ */}
          {!isAuthed && <label className={classes.caution}>{t('mfa.selectMethod.cautionReLogin')}</label>}

          {/* ログイン後のMFA変更時に表示する注意メッセージ */}
          {isAuthed && (
            <>
              <Alert severity="warning">
                <AlertTitle>{t('mfa.caution')}</AlertTitle>
                <Trans i18nKey={'mfa.selectMethod.cautionDetail'} />
              </Alert>
              <Alert severity="warning">
                <AlertTitle>{t('mfa.recommendation')}</AlertTitle>
                <Trans i18nKey={'mfa.selectMethod.recommendationDetail'} />
              </Alert>
            </>
          )}
        </Stack>
        <Stack spacing={2} sx={{ paddingTop: 5 }}>
          {/* ログイン後のMFA変更時：同意確認チェックボックス */}
          {isAuthed && (
            <FormControlLabel
              control={<Checkbox {...getFieldProps('confirmChange')} checked={values.confirmChange} onChange={handleChangeConfirm} />}
              label={t('mfa.selectMethod.confirmChange')}
            />
          )}
          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            className={classes.root}
            disabled={isAuthed && !isConfirmed}>
            {t('button.select')}
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
};
